import { Badge } from '@/components/ui/badge'
import { CardAction, CardTitle } from '@/components/ui/card'
import { ListCard } from '@/components/list-card'
import { ThreadSearchResult } from '@/hooks/use-thread-search'
import {
  ThreadFileChips,
  ThreadObjectChips,
  ThreadUpdatesDigest,
} from './thread-glance'
import { RelativeTime } from './relative-time'
import { cn } from '@/lib/utils'

interface ThreadSearchResultProps {
  result: ThreadSearchResult
  onClick: (threadId: string) => void
}

const MATCH_LABELS: Record<string, string> = {
  semantic: 'Semantic',
  keyword: 'Keyword',
  both: 'Hybrid',
}

export function ThreadSearchResultCard({ result, onClick }: ThreadSearchResultProps) {
  const matchLabel =
    MATCH_LABELS[result.match_type] ||
    result.match_type.charAt(0).toUpperCase() + result.match_type.slice(1)
  const score = Math.round((result.similarity_score || 0) * 100)
  const reasons = result.match_reasons || []
  const visibleReasons = reasons.slice(0, 2)
  const reasonOverflow = reasons.length - visibleReasons.length
  const title = result.subject || 'Untitled thread'

  const header = (
    <>
      <CardTitle className="min-w-0 flex-1 truncate text-base font-semibold leading-tight" title={title}>
        {title}
      </CardTitle>
      <CardAction>
        <Badge variant="secondary" className="shrink-0 gap-1 px-1.5 text-xs font-medium">
          <span className="tabular-nums">{score}%</span>
          <span className="text-muted-foreground">{matchLabel}</span>
        </Badge>
      </CardAction>
    </>
  )

  const body = (
    <div className="flex flex-col gap-2.5">
      {result.summary ? (
        <p className="line-clamp-3 text-sm leading-snug text-muted-foreground">{result.summary}</p>
      ) : null}

      <ThreadUpdatesDigest updates={result.updates_digest} />

      {visibleReasons.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {visibleReasons.map((reason, i) => (
            <Badge
              key={i}
              variant="outline"
              className="px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
            >
              {reason}
            </Badge>
          ))}
          {reasonOverflow > 0 ? (
            <span className="text-xs text-muted-foreground">+{reasonOverflow} more</span>
          ) : null}
        </div>
      ) : null}

      <ThreadObjectChips
        objects={result.objects}
        activity={result.activity}
        objectCount={result.object_count}
      />

      <ThreadFileChips files={result.files} fileCount={result.file_count} />
    </div>
  )

  const footer = (
    <div className="border-t border-border/40 pt-2.5 text-xs text-muted-foreground">
      <span className="tabular-nums">{result.message_count}</span>{' '}
      {result.message_count === 1 ? 'message' : 'messages'}
      <span className="mx-1.5 text-border" aria-hidden>
        ·
      </span>
      <RelativeTime value={result.updated_at} prefix="Updated" />
    </div>
  )

  return (
    <ListCard
      onOpen={() => onClick(result.id)}
      openAriaLabel={`Open thread ${title}`}
      header={header}
      body={body}
      footer={footer}
      className={cn('group h-full')}
      footerClassName="mt-auto border-t-0 px-0"
    />
  )
}
